// Import React to define the component
import React, { useState, useEffect } from "react";
import Spinner from 'react-bootstrap/Spinner';


const calcStats = (shots, key) => {
  const values = shots.map((shot) => parseFloat(shot[key])).filter((v) => !isNaN(v));
  if (!values.length) return { avg: "-", std: "-" };
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const std = Math.sqrt(values.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / values.length);
  return {
    avg: avg.toFixed(2),
    std: std.toFixed(2),
  };
};


// Component receives props for color type (e.g., "White", "Black")
const PrescriptionDataPoints = ({ colorType = "White", appdata, imageURL, shotStats }) => {

  const headers = [
    "vertStrike", "horzStrike", "ballSpeed", "efficiency", "carry",
    "launch", "backSpin", "peakHeight", "decentAngle", "startLine",
    "sideSpin", "faceToPath", "offline", "aoa", "clubPath",
    "clubHeadSpeed", "dynamicLoft",
  ];

  const headerTitles = [
    "Vert. Strike", "Horz. Strike", "Ball Speed", "Efficiency", "Carry",
    "Launch", "Back Spin", "Peak Height", "Decent Angle", "Start Line",
    "Side Spin", "Face To Path", "Offline", "AOA", "Club Path",
    "Club Head Speed", "Dynamic Loft",
  ];

  const clubSections = [`${colorType} 1`, `${colorType} 2`, `${colorType} 3`];

  if (!imageURL) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
        <Spinner animation="border" role="status" variant="light">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="container px-0 py-0">
      {clubSections.map((club, idx) => {
        const shots = appdata?.[club] || [];
        const stat = shotStats?.find(item => item.clubName === club);
        return (
          <div
            key={idx}
            className="table-responsive mb-4 scroll-x-thin"
            style={{ overflowX: "auto", backgroundColor: "#1f1f24", borderRadius: "5px" }}
          >
            <table className="table table-bordered text-white m-0" style={{ minWidth: "1200px", borderColor: "#444" }}>
              <thead>
                <tr>
                  <th className="text-white">
                    <div className="d-flex align-items-center justify-content-center gap-1">
                      <span className="fs-custom"></span>
                      <span className="club-custom">{club}</span>
                    </div>
                  </th>
                  {headerTitles.map((header, i) => (
                    <th key={i} className="text-white text-center">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[0, 1, 2].map((shotIndex) => (
                  <tr className="light-row" key={shotIndex}>
                    <td className="text-white fw-bold light-row">Shot {shotIndex + 1}</td>
                    {headers.map((key, colIndex) => (
                      <td key={colIndex} className="text-center">
                        {/* {shots[shotIndex]?.[key] ?? "-"} */}
                        {shots[shotIndex]?.[key] !== null && shots[shotIndex]?.[key] !== undefined
                          ? parseFloat(shots[shotIndex][key]).toFixed(2)
                          : "-"}
                      </td>
                    ))}
                  </tr>
                ))}

                {/* Average row */}
                <tr>
                  <td className="fw-bold text-white dark-row">Average</td>
                  {headers.map((key, i) => { 
                    let statKey = key;  
                    if (statKey === "vertStrike") statKey = "VerticalLaunchAngle";
                    if (statKey === "horzStrike") statKey = "FaceImpactLateral";
                    if (statKey === "ballSpeed") statKey = "BallSpeed";
                    if (statKey === "efficiency") statKey = "efficiency";
                    if (statKey === "carry") statKey = "CarryDistance";
                    if (statKey === "launch") statKey = "VerticalLaunchAngle";
                    if (statKey === "backSpin") statKey = "BackSpin";
                    if (statKey === "peakHeight") statKey = "PeakHeight";
                    if (statKey === "decentAngle") statKey = "LandingAngle";
                    if (statKey === "startLine") statKey = "StartLine";
                    if (statKey === "sideSpin") statKey = "SideSpin";
                    if (statKey === "faceToPath") statKey = "FaceToTarget";
                    if (statKey === "offline") statKey = "Offline";
                    if (statKey === "aoa") statKey = "AngleOfAttack";
                    if (statKey === "clubPath") statKey = "ClubPath";
                    if (statKey === "clubHeadSpeed") statKey = "ClubHeadSpeed";
                    if (statKey === "dynamicLoft") statKey = "DynamicLoft";


                    const avg = stat?.[statKey]?.average !== undefined ? stat[statKey].average : "-";
                    return (
                      <td key={i} className="text-center">
                        {avg !== "-" ? Number(avg).toFixed(2) : "-"}
                      </td>
                    );
                  })}
                </tr>

                {/* Std. Dev row */}
                <tr>
                  <td className="fw-bold text-white dark-row">Std. Dev</td>
                  {headers.map((key, i) => {
                    let statKey = key;
                    if (statKey === "vertStrike") statKey = "VerticalLaunchAngle";
                    if (statKey === "horzStrike") statKey = "FaceImpactLateral";
                    if (statKey === "ballSpeed") statKey = "BallSpeed";
                    if (statKey === "efficiency") statKey = "efficiency";
                    if (statKey === "carry") statKey = "CarryDistance";
                    if (statKey === "launch") statKey = "VerticalLaunchAngle";
                    if (statKey === "backSpin") statKey = "BackSpin";
                    if (statKey === "peakHeight") statKey = "PeakHeight";
                    if (statKey === "decentAngle") statKey = "LandingAngle";
                    if (statKey === "startLine") statKey = "StartLine";
                    if (statKey === "sideSpin") statKey = "SideSpin";
                    if (statKey === "faceToPath") statKey = "FaceToTarget";
                    if (statKey === "offline") statKey = "Offline";
                    if (statKey === "aoa") statKey = "AngleOfAttack";
                    if (statKey === "clubPath") statKey = "ClubPath";
                    if (statKey === "clubHeadSpeed") statKey = "ClubHeadSpeed";
                    if (statKey === "dynamicLoft") statKey = "DynamicLoft";

                    const std = stat?.[statKey]?.stdDev !== undefined ? stat[statKey].stdDev : "-";
                    return (
                      <td key={i} className="text-center">
                        {std !== "-" ? Number(std).toFixed(2) : "-"}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>

            {/* Same style block as before */}
            <style>
              {`
                /* Thin scrollbar styles for WebKit browsers (Chrome, Safari) */
                  .scroll-x-thin::-webkit-scrollbar {
                    height: 6px;               /* Horizontal scrollbar height */
                    width: 6px;                /* Vertical scrollbar width */
                  }

                  .scroll-x-thin::-webkit-scrollbar-track {
                    background: transparent;   /* Optional: transparent track */
                  }

                  .scroll-x-thin::-webkit-scrollbar-thumb {
                    background-color: #888;    /* Scrollbar thumb color */
                    border-radius: 10px;       /* Rounded corners */
                  }

                  .scroll-x-thin::-webkit-scrollbar-thumb:hover {
                    background-color: #555;    /* Thumb color on hover */
                  }

                  /* Firefox scrollbar styling */
                  .scroll-x-thin {
                    scrollbar-width: thin;                     /* Thin scrollbar width */
                    scrollbar-color: #99ea24 transparent !important;         /* Thumb and track color */
                  }

                  .table-responsive .table tbody tr .dark-row {
                      background-color: #1a1e23 !important;
                  }
                  .table-responsive .table tbody tr.light-row {
                      background-color: #21252e !important;
                  }
                  .table-responsive .table tbody tr .light-row {
                      background-color: #21252e !important;
                  }
                  .fs-custom{
                      background-color: #ffffff !important;
                      border-radius: 50%;
                      display: inline-block;
                      width: 20px;
                      height: 20px;
                  }

                  @media (max-width: 990px) {
                      .container.px-0.py-3{
                          margin: 0px !important;
                          min-width: -webkit-fill-available;
                      }
                  }
                  @media screen and (min-width: 576px) and (max-width: 768px) {
                      .container.px-0.py-3{
                          margin: 0px !important;
                          min-width: -webkit-fill-available;
                      }
                  }
            `}
            </style>
          </div>
        );
      })}
    </div>
  );
};

export default PrescriptionDataPoints;
